import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { theme } from '../../../styles/theme';

export const StyledMarkdownImage = styled('img')({
  maxWidth: '100%',
  height: 'auto',
  display: 'block',
  margin: '16px 0',
});

export const StyledMarkdownVideo = styled('video')({
  maxWidth: '100%',
  height: 'auto',
  display: 'block',
  margin: '16px 0',
});

export const StyledArticleContainer = styled(Box)({
  padding: 24,
});

export const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 24,
});

export const StyledErrorContainer = styled(Box)({
  padding: 24,
});

export const StyledArticleContentBox = styled(Box)({
  textAlign: 'justify',
  padding: '32px 600px 32px 32px',
});

export const articleTitleSx: SxProps<Theme> = {
  marginLeft: 2,
};

// Custom code style for syntax highlighting
export const customCodeStyle = {
  'code[class*="language-"]': {
    color: theme.palette.code.text,
    background: 'none',
    fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
    fontSize: '1em',
    textAlign: 'left' as const,
    whiteSpace: 'pre' as const,
    wordSpacing: 'normal',
    wordBreak: 'normal' as const,
    wordWrap: 'normal' as const,
    lineHeight: '1.5',
    tabSize: 4,
    hyphens: 'none' as const,
  },
  'pre[class*="language-"]': {
    color: theme.palette.code.text,
    background: theme.palette.code.background,
    fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
    fontSize: '1em',
    textAlign: 'left' as const,
    whiteSpace: 'pre' as const,
    wordSpacing: 'normal',
    wordBreak: 'normal' as const,
    wordWrap: 'normal' as const,
    lineHeight: '1.5',
    tabSize: 4,
    hyphens: 'none' as const,
    padding: '1em',
    margin: '.5em 0',
    overflow: 'auto',
    borderRadius: '0.3em',
  },
};

// Sx props for markdown content styling
export const markdownContentSx: SxProps<Theme> = (theme) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: 'text.primary',
    marginTop: 2,
    marginBottom: 1,
  },
  '& p': {
    color: 'text.secondary',
    marginBottom: 2,
    lineHeight: 1.6,
  },
  '& code': {
    backgroundColor: theme.palette.code.background,
    color: theme.palette.code.text,
    padding: '2px 4px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  '& pre': {
    margin: '16px 0',
    borderRadius: 1,
    overflow: 'auto',
    '& code': {
      backgroundColor: `${theme.palette.code.background} !important`,
    },
  },
  '& ul, & ol': {
    color: 'text.secondary',
    paddingLeft: 3,
  },
  '& li': {
    marginBottom: 0.5,
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'primary.main',
    paddingLeft: 2,
    margin: '16px 0',
    fontStyle: 'italic',
    color: 'text.secondary',
  },
  '& details': {
    margin: '16px 0',
  },
  '& summary': {
    padding: '12px 0',
    cursor: 'pointer',
    fontWeight: 'medium',
    color: 'text.primary',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    listStyle: 'none',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
    '&::marker': {
      display: 'none',
    },
    '&::-webkit-details-marker': {
      display: 'none',
    },
    '& h2': {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: 'medium',
    },
  },
  '& details > *:not(summary):not(p)': {
    padding: '16px',
  },
  '& details > p': {
    padding: 0,
    margin: '16px',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    margin: '16px 0',
  },
  '& video': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    margin: '16px 0',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '16px 0',
    overflow: 'hidden',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
  },
  '& thead': {
    backgroundColor: 'action.hover',
  },
  '& th': {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: 'text.primary',
    borderBottom: '2px solid',
    borderBottomColor: 'divider',
  },
  '& td': {
    padding: '12px 16px',
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
  },
  '& tbody tr:last-child td': {
    borderBottom: 'none',
  },
  '& tbody tr:hover': {
    backgroundColor: 'action.hover',
  },
});
